import React, { useState, useEffect } from 'react';
import { Store, Users, CreditCard, RefreshCw, Loader, AlertCircle, Clock } from 'lucide-react';
import adminService, { DashboardStats, RecentActivity } from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [statsData, activitiesData] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getRecentActivities(10)
      ]);

      setStats(statsData);
      setActivities(activitiesData);

      if (showToast) {
        toast.success('Dashboard refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch dashboard data');
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };



  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <Users size={16} />;
      case 'business':
        return <Store size={16} />;
      case 'transaction':
      case 'payment':
        return <CreditCard size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: any = {
      active: 'bg-success-50 text-success-600',
      approved: 'bg-success-50 text-success-600',
      pending: 'bg-warning-50 text-warning-600',
      rejected: 'bg-danger-50 text-danger-600',
      suspended: 'bg-danger-50 text-danger-600',
      completed: 'bg-success-50 text-success-600',
      failed: 'bg-danger-50 text-danger-600',
    };
    return styles[status.toLowerCase()] || 'bg-secondary-50 text-secondary-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20">
        <p className="text-secondary-600 mb-4">Failed to load dashboard data</p>
        <button
          onClick={() => fetchDashboardData()}
          className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-1">Dashboard</h1>
          <p className="text-secondary-600 text-sm md:text-base">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={() => fetchDashboardData(true)}
          disabled={refreshing}
          className="w-full sm:w-auto bg-primary-50 hover:bg-primary-100 text-primary-600 px-4 py-2 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Businesses */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-secondary-500 text-sm mb-1">Total Businesses</p>
            <p className="text-2xl font-bold text-secondary-900">{stats.total_businesses}</p>
          </div>
          <div className="bg-primary-500 p-3 rounded-full">
            <Store className="text-white" size={20} />
          </div>
        </div>

        {/* Total Users */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-secondary-500 text-sm mb-1">Total Users</p>
            <p className="text-2xl font-bold text-secondary-900">{stats.total_users}</p>
          </div>
          <div className="bg-success-500 p-3 rounded-full">
            <Users className="text-white" size={20} />
          </div>
        </div>

        {/* Today's Transactions */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-secondary-500 text-sm mb-1">Today's Transactions</p>
            <p className="text-2xl font-bold text-secondary-900">{stats.today_transactions}</p>
            {stats.failed_transactions > 0 && (
              <p className="text-xs text-danger-600 mt-1">{stats.failed_transactions} Failed</p>
            )}
          </div>
          <div className="bg-blue-500 p-3 rounded-full">
            <CreditCard className="text-white" size={20} />
          </div>
        </div>


      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        {/* Pending Approvals */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-secondary-500 text-sm mb-1">Pending Approvals</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending_approvals}</p>
          </div>
          <div className="bg-amber-100 p-3 rounded-full">
            <Clock className="text-amber-600" size={20} />
          </div>
        </div>


        {/* Suspended Businesses */}
        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-secondary-500 text-sm mb-1">Suspended Businesses</p>
            <p className="text-2xl font-bold text-gray-600">{stats.suspended_businesses}</p>
          </div>
          <div className="bg-gray-100 p-3 rounded-full">
            <Store className="text-gray-600" size={20} />
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-secondary-900">Recent Activities</h2>
          <span className="text-xs font-semibold text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full">
            LAST {activities.length}
          </span>
        </div>

        {activities.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-secondary-100 p-4 rounded-full inline-block mb-4">
              <AlertCircle size={48} className="text-secondary-400" />
            </div>
            <p className="text-secondary-600">No recent activities</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 hover:bg-secondary-50 rounded-lg transition-colors border-l-4 gap-4"
                style={{
                  borderLeftColor:
                    activity.activity_type === 'user' ? '#22c55e' :
                      activity.activity_type === 'business' ? '#3b82f6' :
                        '#f59e0b'
                }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${activity.activity_type === 'user' ? 'bg-success-50' :
                    activity.activity_type === 'business' ? 'bg-primary-50' :
                      'bg-warning-50'
                    }`}>
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 text-sm md:text-base">{activity.activity_name}</p>
                    <p className="text-xs md:text-sm text-secondary-600">{activity.entity_name}</p>
                  </div>
                </div>
                <div className="text-left sm:text-right w-full sm:w-auto flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(activity.activity_status)}`}>
                    {activity.activity_status}
                  </span>
                  <p className="text-[10px] md:text-xs text-secondary-500 sm:mt-1">{getTimeAgo(activity.activity_date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
